import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MessageSquare, Send, Image as ImageIcon, Video, BarChart3, LogOut, Plus,
  Users, X, AtSign, ArrowLeft, Loader2, MapPin, Sparkles,
} from 'lucide-react';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'm4v'];

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                 : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CreatePollDialog({ open, onClose, onSubmit, lang }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState([{ id: 'opt-1', text: '' }, { id: 'opt-2', text: '' }]);
  const isID = lang !== 'en';
  const submit = () => {
    const filled = options.map(o => o.text.trim()).filter(Boolean);
    if (!question.trim() || filled.length < 2) {
      return toast.error(isID ? 'Pertanyaan & minimal 2 opsi wajib diisi' : 'Question and at least 2 options required');
    }
    onSubmit(question.trim(), filled);
    setQuestion(''); setOptions([{ id: 'opt-1', text: '' }, { id: 'opt-2', text: '' }]);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="glass-card max-w-md" data-testid="poll-dialog">
        <DialogHeader><DialogTitle className="t-text">{isID ? 'Buat Polling' : 'Create Poll'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder={isID ? 'Pertanyaan *' : 'Question *'} value={question} onChange={e => setQuestion(e.target.value)} data-testid="poll-question" />
          {options.map((o, i) => (
            <div key={o.id} className="flex gap-2">
              <Input placeholder={`${isID ? 'Opsi' : 'Option'} ${i + 1} ${i < 2 ? '*' : ''}`}
                value={o.text}
                onChange={e => setOptions(prev => prev.map(p => p.id === o.id ? { ...p, text: e.target.value } : p))}
                data-testid={`poll-option-${i}`}
              />
              {options.length > 2 && (
                <Button variant="ghost" size="icon" onClick={() => setOptions(prev => prev.filter(p => p.id !== o.id))} data-testid={`poll-remove-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {options.length < 6 && (
            <Button variant="outline" onClick={() => setOptions(prev => [...prev, { id: `opt-${Date.now()}-${prev.length + 1}`, text: '' }])} className="w-full" data-testid="poll-add-option">
              <Plus className="w-3.5 h-3.5 mr-1" />{isID ? 'Tambah opsi' : 'Add option'}
            </Button>
          )}
          <Button onClick={submit} className="w-full bg-[#D98A94] text-white" data-testid="poll-submit">
            {isID ? 'Kirim Polling' : 'Send Poll'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PollView({ poll, onVote, myAccountId, lang }) {
  if (!poll) return null;
  const totalVotes = poll.options.reduce((s, o) => s + o.votes.length, 0);
  const myVote = poll.options.findIndex(o => o.votes.includes(myAccountId));
  return (
    <div className="mt-2 rounded-lg overflow-hidden" style={{ background: 'rgba(217,138,148,0.06)', border: '1px solid rgba(217,138,148,0.25)' }}>
      <div className="px-3 py-2 font-semibold t-text text-sm flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5 text-[#D98A94]" />{poll.question}
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        {poll.options.map((opt, i) => {
          const pct = totalVotes ? Math.round((opt.votes.length / totalVotes) * 100) : 0;
          const isMy = myVote === i;
          return (
            <button key={`${poll.id}-opt-${i}`} onClick={() => onVote(i)} data-testid={`poll-vote-${i}`}
              className={`w-full text-left px-3 py-2 relative overflow-hidden transition ${isMy ? 'font-semibold' : ''}`}>
              <div className="absolute inset-0" style={{ width: `${pct}%`, background: isMy ? 'rgba(217,138,148,0.20)' : 'rgba(217,138,148,0.10)' }} />
              <div className="relative flex justify-between items-center text-xs t-text">
                <span>{opt.text}</span>
                <span className="text-[10px] t-text-muted">{opt.votes.length} · {pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="px-3 py-1.5 text-[10px] t-text-muted">
        {totalVotes} {lang === 'en' ? 'votes' : 'suara'}
      </div>
    </div>
  );
}

function ProfileMiniCard({ card, compact }) {
  if (!card) return null;
  const genderLbl = card.gender === 'male' ? 'L' : card.gender === 'female' ? 'P' : '';
  const bits = [];
  if (card.age) bits.push(`${card.age} thn${genderLbl ? ' ' + genderLbl : ''}`);
  if (card.city) bits.push(card.city);
  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'p-2 rounded-xl glass-card'}`}>
      {card.photo_url
        ? <img src={card.photo_url} alt={card.name} className="w-10 h-10 rounded-full object-cover border border-[#D98A94]/30" loading="lazy" />
        : <div className="w-10 h-10 rounded-full bg-[#D98A94]/15 flex items-center justify-center text-[#D98A94] text-xs font-bold">{(card.name || '?').slice(0, 1)}</div>}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold t-text truncate">{card.name}</div>
        <div className="text-[10px] t-text-muted truncate">{bits.join(' · ')}</div>
        {(card.education || card.occupation) && (
          <div className="text-[10px] t-text-muted truncate">{[card.education, card.occupation].filter(Boolean).join(' · ')}</div>
        )}
      </div>
    </div>
  );
}

function SystemWelcomeMessage({ msg }) {
  const card = msg.profile_card;
  return (
    <div className="my-3 mx-auto max-w-md" data-testid={`msg-${msg.id}`}>
      <div className="rounded-2xl p-3 border border-[#D98A94]/30" style={{ background: 'linear-gradient(135deg, rgba(217,138,148,0.10), rgba(199,134,122,0.06))' }}>
        <div className="flex items-center gap-1 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-[#D98A94]" />
          <span className="text-[10px] font-semibold t-text-secondary uppercase tracking-wide">Anggota Baru</span>
        </div>
        {card && <ProfileMiniCard card={card} />}
        <div className="mt-2 text-xs t-text whitespace-pre-line">{msg.text}</div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, poll, isMe, onVote, myAccountId, lang, mediaAuthQuery }) {
  const align = isMe ? 'items-end' : 'items-start';
  const bg = isMe ? 'bg-[#D98A94] text-white' : 't-bg-input t-text';
  const mediaSrc = msg.media_url ? (msg.media_url.includes('?') ? msg.media_url : `${msg.media_url}${mediaAuthQuery}`) : '';
  return (
    <div className={`flex flex-col ${align} mb-3`} data-testid={`msg-${msg.id}`}>
      {!isMe && <span className="text-[10px] t-text-muted mb-0.5 px-1">{msg.author_name}</span>}
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${bg} shadow-sm`}>
        {msg.media_type === 'image' && mediaSrc && (
          <img src={mediaSrc} alt="chat media" className="rounded-lg mb-1 max-w-full" loading="lazy" />
        )}
        {msg.media_type === 'video' && mediaSrc && (
          <video src={mediaSrc} controls className="rounded-lg mb-1 max-w-full" preload="metadata" />
        )}
        {msg.text && !msg.poll_id && (
          <div className="text-sm whitespace-pre-wrap break-words">
            {msg.text.split(/(\s+)/).map((w, i) => w.startsWith('@')
              ? <span key={`${msg.id}-tok-${i}`} className={isMe ? 'font-semibold underline' : 'text-[#D98A94] font-semibold'}>{w}</span>
              : w)}
          </div>
        )}
        {msg.poll_id && poll && <PollView poll={poll} onVote={onVote} myAccountId={myAccountId} lang={lang} />}
        <div className={`text-[9px] mt-1 opacity-70 ${isMe ? 'text-right' : ''}`}>{fmtTime(msg.created_at)}</div>
      </div>
    </div>
  );
}

/**
 * Suite chat panel.
 *
 * Props:
 *   API        — REST base (e.g. https://.../api)
 *   scope      — 'buddhist' | 'katolik'
 *   token      — JWT for auth (used for both REST and WebSocket)
 *   accountId  — logged-in member account id (to detect "my" messages)
 *   lang       — 'en' | 'id'
 */
export default function SuiteChatPanel({ API, scope, token, accountId, lang }) {
  const isID = lang !== 'en';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const mediaAuthQuery = useMemo(() => `?token=${encodeURIComponent(token || '')}`, [token]);
  const wsProto = useMemo(() => {
    if (!API) return 'wss';
    try { return new URL(API).protocol === 'https:' ? 'wss' : 'ws'; } catch { return 'wss'; }
  }, [API]);
  const wsHost = useMemo(() => {
    if (!API) return '';
    try { return new URL(API).host; } catch { return ''; }
  }, [API]);

  const [groups, setGroups] = useState({ joined: [], available: [], max_groups: 3 });
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [polls, setPolls] = useState({});
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showJoinList, setShowJoinList] = useState(false);
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const [members, setMembers] = useState([]);
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const fileImgRef = useRef(null);
  const fileVidRef = useRef(null);

  // --- Load groups ---
  const loadGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/suites/${scope}/chat/groups`, { headers: authHeaders });
      setGroups({ joined: r.data.joined, available: r.data.available, max_groups: r.data.max_groups });
      if (!activeGroup && r.data.joined.length > 0) setActiveGroup(r.data.joined[0]);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Gagal memuat grup' : 'Failed to load groups'));
    }
  }, [API, scope, authHeaders, activeGroup, isID]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // --- Load messages + members when active group changes ---
  const loadMessages = useCallback(async (groupId) => {
    try {
      const r = await axios.get(`${API}/suites/${scope}/chat/groups/${groupId}/messages`, { headers: authHeaders });
      setMessages(r.data.messages || []);
      setPolls(r.data.polls || {});
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  }, [API, scope, authHeaders]);

  const loadMembers = useCallback(async (groupId) => {
    try {
      const r = await axios.get(`${API}/suites/${scope}/chat/groups/${groupId}/members`, { headers: authHeaders });
      setMembers(r.data.members || []);
    } catch { /* ignore */ }
  }, [API, scope, authHeaders]);

  useEffect(() => {
    if (!activeGroup) return;
    loadMessages(activeGroup.id);
    loadMembers(activeGroup.id);
  }, [activeGroup, loadMessages, loadMembers]);

  // --- WebSocket ---
  useEffect(() => {
    if (!activeGroup || !token || !wsHost) return;
    const url = `${wsProto}://${wsHost}/api/ws/suites/${scope}/chat/${activeGroup.id}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch { return; }
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === 'message') {
          setMessages(prev => [...prev, payload.message]);
          if (payload.message.poll_id && payload.message.poll) {
            setPolls(prev => ({ ...prev, [payload.message.poll_id]: payload.message.poll }));
          }
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 30);
        } else if (payload.type === 'poll_update') {
          setPolls(prev => ({ ...prev, [payload.poll.id]: payload.poll }));
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => { wsRef.current = null; };
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [activeGroup, token, wsHost, wsProto, scope]);

  // --- Send text ---
  const sendText = async () => {
    const t = text.trim();
    if (!t || !activeGroup || sending) return;
    setSending(true);
    // Detect mentions like @name
    const mentions = members
      .filter(m => new RegExp(`@${m.name.split(' ')[0]}`, 'i').test(t))
      .map(m => m.id);
    try {
      await axios.post(`${API}/suites/${scope}/chat/groups/${activeGroup.id}/messages`, { text: t, mentions }, { headers: authHeaders });
      setText('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.moderated) {
        toast.error(detail.message, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed');
      }
    } finally { setSending(false); }
  };

  // --- Upload media ---
  const upload = async (file) => {
    if (!file || !activeGroup) return;
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (![...IMAGE_EXTS, ...VIDEO_EXTS].includes(ext)) {
      return toast.error(isID ? 'Format tidak didukung' : 'Unsupported format');
    }
    const isImage = IMAGE_EXTS.includes(ext);
    const cap = isImage ? 30 : 300;
    if (file.size > cap * 1024 * 1024) {
      return toast.error(isID ? `File terlalu besar (max ${cap} MB)` : `File too large (max ${cap} MB)`);
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/suites/${scope}/chat/groups/${activeGroup.id}/upload`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileImgRef.current) fileImgRef.current.value = '';
      if (fileVidRef.current) fileVidRef.current.value = '';
    }
  };

  // --- Poll ---
  const submitPoll = async (question, options) => {
    if (!activeGroup) return;
    try {
      await axios.post(`${API}/suites/${scope}/chat/groups/${activeGroup.id}/poll`, { question, options }, { headers: authHeaders });
      setShowPollDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const votePoll = async (pollId, idx) => {
    try {
      const r = await axios.post(`${API}/suites/${scope}/chat/polls/${pollId}/vote`, { option_index: idx }, { headers: authHeaders });
      setPolls(prev => ({ ...prev, [pollId]: r.data.poll }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // --- Join / leave ---
  const joinGroup = async (g) => {
    try {
      await axios.post(`${API}/suites/${scope}/chat/groups/${g.id}/join`, {}, { headers: authHeaders });
      await loadGroups();
      setActiveGroup(g);
      setShowJoinList(false);
      toast.success(isID ? `Berhasil bergabung ke ${g.name}` : `Joined ${g.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const leaveGroup = async (g) => {
    if (!window.confirm(isID ? `Keluar dari grup ${g.name}?` : `Leave ${g.name}?`)) return;
    try {
      await axios.post(`${API}/suites/${scope}/chat/groups/${g.id}/leave`, {}, { headers: authHeaders });
      if (activeGroup?.id === g.id) setActiveGroup(null);
      await loadGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // --- Mention menu ---
  const onTextChange = (v) => {
    setText(v);
    const lastAt = v.lastIndexOf('@');
    setShowMentionMenu(lastAt >= 0 && (lastAt === 0 || v[lastAt - 1] === ' '));
  };
  const insertMention = (name) => {
    const idx = text.lastIndexOf('@');
    setText(text.substring(0, idx) + '@' + name.split(' ')[0] + ' ');
    setShowMentionMenu(false);
  };

  const canJoinMore = groups.joined.filter(g => g.group_type !== 'province').length < groups.max_groups;
  // Always allow re-joining a province group even at the city cap — province groups don't count.
  const hasProvinceAvailable = groups.available.some(g => g.group_type === 'province');
  const showJoinBtn = canJoinMore || hasProvinceAvailable;

  return (
    <div className="glass-card rounded-2xl overflow-hidden flex flex-col" style={{ height: 'min(80vh, 720px)' }} data-testid={`chat-panel-${scope}`}>
      {/* Header: joined groups */}
      <div className="flex items-center gap-2 px-3 py-2 border-b overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        {groups.joined.length === 0 && (
          <div className="text-xs t-text-muted flex items-center gap-2 px-2 py-1">
            <MessageSquare className="w-3.5 h-3.5" />
            {isID ? 'Anda belum tergabung di grup mana pun.' : 'You are not in any group yet.'}
          </div>
        )}
        {groups.joined.map(g => {
          const isProvince = g.group_type === 'province';
          return (
            <button key={g.id} onClick={() => setActiveGroup(g)} data-testid={`group-tab-${g.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition ${activeGroup?.id === g.id ? 'bg-[#D98A94] text-white' : 't-bg-input t-text-secondary'} ${isProvince ? 'border border-[#D98A94]/40' : ''}`}
              title={isProvince ? (isID ? 'Grup provinsi (otomatis)' : 'Province group (auto)') : ''}>
              {isProvince ? <MapPin className="w-3 h-3" /> : <Users className="w-3 h-3" />}
              {isProvince ? (g.province || g.city) : g.city} <span className="opacity-70">({g.member_count})</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1">
          {activeGroup && (
            <Button size="sm" variant="ghost" onClick={() => setShowMemberList(true)} data-testid="chat-open-members" title={isID ? 'Lihat anggota' : 'View members'}>
              <Users className="w-3.5 h-3.5 mr-1" />{members.length || '·'}
            </Button>
          )}
          {showJoinBtn && (
            <Button size="sm" variant="ghost" onClick={() => setShowJoinList(true)} data-testid="chat-open-join">
              <Plus className="w-3.5 h-3.5 mr-1" />{isID ? 'Join Grup' : 'Join'}
            </Button>
          )}
          {activeGroup && (
            <Button size="sm" variant="ghost" onClick={() => leaveGroup(activeGroup)} data-testid="chat-leave-active">
              <LogOut className="w-3.5 h-3.5 mr-1" />{isID ? 'Keluar' : 'Leave'}
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      {activeGroup ? (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center t-text-muted text-sm">
                <MessageSquare className="w-8 h-8 mb-2 opacity-40" />
                {isID ? 'Belum ada pesan. Sapa dulu yuk!' : 'No messages yet. Say hi!'}
              </div>
            )}
            {messages.map(m => m.is_system && m.system_type === 'welcome'
              ? <SystemWelcomeMessage key={m.id} msg={m} />
              : (
                <MessageBubble
                  key={m.id}
                  msg={m}
                  poll={polls[m.poll_id]}
                  isMe={m.author_id === accountId}
                  onVote={(idx) => votePoll(m.poll_id, idx)}
                  myAccountId={accountId}
                  lang={lang}
                  mediaAuthQuery={mediaAuthQuery}
                />
              ))}
          </div>

          {/* Input */}
          <div className="border-t px-3 py-2 relative" style={{ borderColor: 'var(--border-color)' }}>
            {showMentionMenu && members.length > 0 && (
              <div className="absolute bottom-full left-3 right-3 mb-1 max-h-40 overflow-y-auto rounded-lg glass-card shadow-lg z-20">
                {members.slice(0, 8).map(m => (
                  <button key={m.id} onClick={() => insertMention(m.name)} data-testid={`mention-${m.id}`}
                    className="w-full text-left px-3 py-2 text-xs t-text hover:bg-[var(--bg-input)] flex items-center gap-2">
                    <AtSign className="w-3 h-3 text-[#D98A94]" />{m.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <input type="file" accept="image/*" ref={fileImgRef} onChange={e => e.target.files?.[0] && upload(e.target.files[0])} className="hidden" data-testid="chat-file-img" />
              <input type="file" accept="video/*" ref={fileVidRef} onChange={e => e.target.files?.[0] && upload(e.target.files[0])} className="hidden" data-testid="chat-file-vid" />
              <Button variant="ghost" size="icon" onClick={() => fileImgRef.current?.click()} disabled={uploading} data-testid="chat-btn-img" title={`${isID ? 'Foto' : 'Photo'} (max 30 MB)`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => fileVidRef.current?.click()} disabled={uploading} data-testid="chat-btn-vid" title={`${isID ? 'Video' : 'Video'} (max 300 MB)`}>
                <Video className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowPollDialog(true)} data-testid="chat-btn-poll" title={isID ? 'Polling' : 'Poll'}>
                <BarChart3 className="w-4 h-4" />
              </Button>
              <Input value={text} onChange={e => onTextChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                placeholder={isID ? 'Ketik pesan… (@ untuk tag)' : 'Type a message… (@ to tag)'}
                data-testid="chat-input" />
              <Button onClick={sendText} disabled={sending || !text.trim()} className="bg-[#D98A94] text-white" data-testid="chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center t-text-muted text-sm p-6">
          <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
          <div>{isID ? 'Pilih grup atau join grup baru untuk mulai chat.' : 'Pick a group or join one to start.'}</div>
          {canJoinMore && (
            <Button onClick={() => setShowJoinList(true)} className="mt-4 bg-[#D98A94] text-white" data-testid="chat-empty-join">
              <Plus className="w-4 h-4 mr-1" />{isID ? 'Join Grup' : 'Join a group'}
            </Button>
          )}
        </div>
      )}

      {/* Join dialog */}
      <Dialog open={showJoinList} onOpenChange={setShowJoinList}>
        <DialogContent className="glass-card max-w-md">
          <DialogHeader><DialogTitle className="t-text">
            {isID ? 'Grup tersedia' : 'Available groups'} ({groups.joined.length}/{groups.max_groups})
          </DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {groups.available.length === 0 && <div className="text-sm t-text-muted text-center py-6">{isID ? 'Tidak ada grup tersedia.' : 'No available groups.'}</div>}
            {groups.available.map(g => (
              <button key={g.id} onClick={() => joinGroup(g)} disabled={!canJoinMore} data-testid={`join-${g.id}`}
                className="w-full flex items-center justify-between p-3 rounded-lg glass-card hover:border-[#D98A94] disabled:opacity-50 transition">
                <div className="text-left">
                  <div className="text-sm font-semibold t-text">{g.name}</div>
                  <div className="text-[10px] t-text-muted">{g.member_count} {isID ? 'anggota' : 'members'}</div>
                </div>
                <ArrowLeft className="w-4 h-4 rotate-180 text-[#D98A94]" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <CreatePollDialog open={showPollDialog} onClose={() => setShowPollDialog(false)} onSubmit={submitPoll} lang={lang} />

      {/* Member list dialog */}
      <Dialog open={showMemberList} onOpenChange={setShowMemberList}>
        <DialogContent className="glass-card max-w-md">
          <DialogHeader><DialogTitle className="t-text">
            <Users className="w-4 h-4 inline mr-1 text-[#D98A94]" />{isID ? 'Anggota Grup' : 'Group Members'} ({members.length})
          </DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {members.length === 0 && <div className="text-sm t-text-muted text-center py-6">{isID ? 'Belum ada anggota.' : 'No members yet.'}</div>}
            {members.map(m => (
              <div key={m.account_id || m.id} className="rounded-xl p-2 t-bg-input" data-testid={`member-row-${m.account_id || m.id}`}>
                <ProfileMiniCard card={m} compact />
                <div className="flex flex-wrap gap-1 mt-1.5 pl-12">
                  {m.category && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#D98A94]/15 text-[#D98A94]">{m.category}</span>}
                  {m.province && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-black/5 t-text-muted">{m.province}</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
